// The signed-in user's REAL Outlook calendar via Microsoft Graph
// (delegated Calendars.Read, admin-consented on the WISE CRM app regs
// 2026-07-14). Replaces the prototype's "Syncs to Outlook" copy that sat
// on top of static seed events.

import type { IPublicClientApplication, AccountInfo } from '@azure/msal-browser';

export interface OutlookEvent {
  id: string;
  subject: string;
  startIso: string; // UTC ISO
  isAllDay: boolean;
  location?: string;
  organizer?: string;
  webLink?: string;
}

export async function fetchOutlookEvents(
  instance: IPublicClientApplication,
  account: AccountInfo,
  days = 90,
): Promise<OutlookEvent[]> {
  let accessToken: string;
  try {
    ({ accessToken } = await instance.acquireTokenSilent({ account, scopes: ['Calendars.Read'] }));
  } catch (err) {
    const { InteractionRequiredAuthError } = await import('@azure/msal-browser');
    if (err instanceof InteractionRequiredAuthError) {
      ({ accessToken } = await instance.acquireTokenPopup({ account, scopes: ['Calendars.Read'] }));
    } else {
      throw err;
    }
  }
  const start = new Date();
  const end = new Date(Date.now() + days * 86_400_000);
  const url = 'https://graph.microsoft.com/v1.0/me/calendarView'
    + `?startDateTime=${encodeURIComponent(start.toISOString())}`
    + `&endDateTime=${encodeURIComponent(end.toISOString())}`
    + '&$select=id,subject,start,end,isAllDay,location,organizer,webLink'
    + '&$orderby=start/dateTime&$top=100';
  const res = await fetch(url, { headers: { Authorization: `Bearer ${accessToken}` } });
  if (!res.ok) throw new Error(`Graph calendarView failed (${res.status})`);
  const body = (await res.json()) as {
    value: Array<{
      id: string; subject: string | null; isAllDay: boolean
      start: { dateTime: string; timeZone: string }
      location?: { displayName?: string }
      organizer?: { emailAddress?: { name?: string } }
      webLink?: string
    }>
  };
  return body.value.map((e) => ({
    id: e.id,
    subject: e.subject ?? '(no subject)',
    // Graph returns naive datetimes in the requested tz (UTC by default).
    startIso: e.start.dateTime.endsWith('Z') ? e.start.dateTime : `${e.start.dateTime}Z`,
    isAllDay: e.isAllDay,
    location: e.location?.displayName || undefined,
    organizer: e.organizer?.emailAddress?.name || undefined,
    webLink: e.webLink,
  }));
}
